import type { TemplatePolicy } from '@safetyhub/shared';
import { findPostSubmitFlow } from '@safetyhub/shared';
import { SELECT_CLS } from './editorFieldClasses';
import {
  POST_SUBMIT_FLOW_OPTIONS,
  applyPostSubmitFlow,
  getPostSubmitFlow,
} from './postSubmitFlowPolicy';

/**
 * 템플릿 전역 속성 — 「제출 후 흐름」 구획(결정 2026-09-06 §1).
 *
 * 저작이 고르는 것은 흐름 하나뿐이고(`policy.postSubmit.flow`), 그 흐름의 화면·진입 조건·설정
 * 완료 판정은 web 레지스트리가 소유한다. 판정·전환 규칙은 `./postSubmitFlowPolicy` 몫 —
 * 여기는 그리기만 한다(「분할 축」 구획과 같은 배치).
 *
 * 「없음」으로 되돌릴 때 확인을 받지 않는다 — 분할 축과 달리 이 축엔 화면 밖 세부 선언이 없어
 * 잃을 것이 없다. 없는 것을 잃는다고 물으면 그 안내가 거짓이 된다.
 */
export function PostSubmitFlowSection({
  policy,
  onChange,
}: {
  policy: TemplatePolicy | undefined;
  onChange: (next: TemplatePolicy) => void;
}) {
  const current = getPostSubmitFlow(policy);
  const selected = findPostSubmitFlow(current);

  return (
    <div className="pt-2 border-t border-gray-100">
      <span className="text-xs font-medium text-gray-600">제출 후 흐름</span>
      <p className="mt-1 text-[11px] text-gray-400">
        작성을 마치면 이어질 화면을 정합니다. 고르면 마지막(또는 단건) 저장 버튼이 「작성 완료 →
        …」로 전환을 예고하고, 저장과 함께 그 화면으로 들어갑니다. 「없음」이면 제출에서 끝납니다.
      </p>
      <label className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-600">
        <span className="shrink-0">이어질 흐름</span>
        {/* 접근명은 감싼 label의 텍스트("이어질 흐름")다 — aria-label로 덮으면 보이는 라벨과
            읽히는 이름이 갈린다(WCAG 2.5.3). 구획 제목이 「제출 후 흐름」 맥락을 준다. */}
        <select
          value={current}
          onChange={(e) => onChange(applyPostSubmitFlow(policy, e.target.value))}
          className={`${SELECT_CLS} max-w-[10rem]`}
        >
          {POST_SUBMIT_FLOW_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      {/* 고른 흐름의 설명 — 「무엇이 이어지는가」는 키 이름만으로는 안 보인다.
          등록되지 않은 키가 저장돼 있으면 select에 맞는 option이 없어 화면은 「없음」을
          보이는데 **저장된 값은 그대로**다. 그 어긋남을 조용히 두면 저작자가 "꺼져 있다"고
          믿는 흐름이 실화면에서 살아 있게 된다. */}
      {selected ? (
        <p className="mt-1 text-[11px] text-gray-500">
          {selected.description} 저장 버튼: 「작성 완료 → {selected.ctaLabel}」
        </p>
      ) : current ? (
        <p className="mt-1 text-[11px] text-amber-600">
          등록되지 않은 흐름 키(<span className="font-mono">{current}</span>)가 저장돼 있습니다 —
          위 칸은 「없음」으로 보이지만 값은 남아 있습니다. 지우려면 「없음」을 고르세요.
        </p>
      ) : null}
    </div>
  );
}
